import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';
import { formatDate } from '@/utils/dateFormat';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [techFormData, setTechFormData] = useState({ name: '', phone: '' });
  const [isAddingTech, setIsAddingTech] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextServiceDates, setNextServiceDates] = useState({});
  const [technicianRatings, setTechnicianRatings] = useState({});
  const [staff, setStaff] = useState([]);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [staffFormData, setStaffFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'technician',
    technician_type: 'in-house'
  });
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [serviceCountdowns, setServiceCountdowns] = useState([]);
  // Pending staff (technicians/marketers awaiting approval)
  const [pendingStaff, setPendingStaff] = useState([]);
  // Merchant management state
  const [merchants, setMerchants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [approvedMerchants, setApprovedMerchants] = useState([]);
  const [orderFormData, setOrderFormData] = useState({
    merchant_id: '',
    pickup_technician_id: '',
    items: [{ type: 'oil', brand: '', quantity: 1 }],
    total_amount: '',
    notes: ''
  });
  
  // Product management state
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState({ categories: [], oil_types: [], service_charge: 10000 });
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    category: 'engine_oil',
    brand_name: '',
    code: '',
    oil_type: '',
    price: '',
    image_url: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Edit user state
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserFormData, setEditUserFormData] = useState({
    name: '', email: '', phone: '', address: '', state: '', lga: ''
  });

  // Edit merchant state
  const [editMerchantDialogOpen, setEditMerchantDialogOpen] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [editMerchantFormData, setEditMerchantFormData] = useState({
    business_name: '', contact_person: '', email: '', phone: '', address: '', oil_brands: '', filter_brands: ''
  });

  // Organizations state
  const [organizations, setOrganizations] = useState([]);
  const [editOrgDialogOpen, setEditOrgDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editOrgFormData, setEditOrgFormData] = useState({
    name: '', organization_type: '', email: '', phone: '', address: '',
    contact_person_name: '', contact_person_phone: '', contact_person_email: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appointmentsRes, usersRes, techniciansRes, staffRes, countdownsRes, merchantsRes, ordersRes, approvedMerchantsRes, pendingStaffRes, productsRes, categoriesRes, organizationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/appointments`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/technicians`),
        axios.get(`${API}/admin/staff`),
        axios.get(`${API}/admin/customers-service-countdown`),
        axios.get(`${API}/admin/merchants`).catch(() => ({ data: [] })),
        axios.get(`${API}/orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/merchants/approved`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pending-staff`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/products`).catch(() => ({ data: [] })),
        axios.get(`${API}/products/categories`).catch(() => ({ data: { categories: [], oil_types: [], service_charge: 10000 } })),
        axios.get(`${API}/admin/organizations`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setAppointments(appointmentsRes.data);
      setUsers(usersRes.data);
      setTechnicians(techniciansRes.data);
      setStaff(staffRes.data);
      setServiceCountdowns(countdownsRes.data);
      setMerchants(merchantsRes.data);
      setOrders(ordersRes.data);
      setApprovedMerchants(approvedMerchantsRes.data);
      setPendingStaff(pendingStaffRes.data);
      setProducts(productsRes.data);
      setProductCategories(categoriesRes.data);
      setOrganizations(organizationsRes.data);
      
      // Fetch ratings for all technicians
      const ratingsPromises = techniciansRes.data.map(tech =>
        axios.get(`${API}/technicians/${tech.id}/ratings`).catch(() => null)
      );
      const ratingsResponses = await Promise.all(ratingsPromises);
      const ratingsMap = {};
      techniciansRes.data.forEach((tech, index) => {
        if (ratingsResponses[index]) {
          ratingsMap[tech.id] = ratingsResponses[index].data;
        }
      });
      setTechnicianRatings(ratingsMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateAppointment = async (appointmentId, status, technicianId = null) => {
    try {
      const payload = { status };
      if (technicianId) payload.technician_id = technicianId;
      
      // If marking as completed, include next service date if provided
      if (status === 'completed' && nextServiceDates[appointmentId]) {
        payload.next_service_date = nextServiceDates[appointmentId];
      }
      
      await axios.put(`${API}/admin/appointments/${appointmentId}`, payload);
      toast.success(status === 'completed' ? 'Appointment completed! Next service date updated.' : 'Appointment updated');
      
      // Clear the next service date for this appointment
      setNextServiceDates(prev => {
        const newDates = { ...prev };
        delete newDates[appointmentId];
        return newDates;
      });
      
      fetchData();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const addTechnician = async (e) => {
    e.preventDefault();
    setIsAddingTech(true);

    try {
      await axios.post(`${API}/admin/technicians`, techFormData);
      toast.success('Technician added');
      setTechFormData({ name: '', phone: '' });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add technician');
    } finally {
      setIsAddingTech(false);
    }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    setIsAddingStaff(true);

    try {
      const payload = { ...staffFormData };
      // Only include technician_type for technician role
      if (staffFormData.role !== 'technician') {
        delete payload.technician_type;
      }
      await axios.post(`${API}/admin/staff`, payload);
      toast.success(`${staffFormData.role === 'technician' ? 'Technician' : staffFormData.role === 'secretary' ? 'Secretary' : 'Marketer'} account created successfully`);
      setStaffFormData({ name: '', email: '', phone: '', password: '', role: 'technician', technician_type: 'in-house' });
      setStaffDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setIsAddingStaff(false);
    }
  };

  // Merchant management functions
  const approveMerchant = async (merchantId) => {
    try {
      await axios.put(`${API}/admin/merchants/${merchantId}/approve`);
      toast.success('Merchant approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve merchant');
    }
  };

  const rejectMerchant = async (merchantId) => {
    const reason = prompt('Enter rejection reason:');
    if (reason === null) return;
    try {
      await axios.put(`${API}/admin/merchants/${merchantId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Merchant rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject merchant');
    }
  };

  const deregisterMerchant = async (merchantId) => {
    if (!confirm('Are you sure you want to deregister this merchant?')) return;
    try {
      await axios.put(`${API}/admin/merchants/${merchantId}/deregister`);
      toast.success('Merchant deregistered');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deregister merchant');
    }
  };

  // Pending Staff (Technician/Marketer) Approval
  const approveStaff = async (userId) => {
    try {
      await axios.put(`${API}/admin/approve-staff/${userId}`);
      toast.success('Staff account approved successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve staff');
    }
  };

  const rejectStaff = async (userId) => {
    if (!confirm('Are you sure you want to reject this application? This will delete their account.')) return;
    try {
      await axios.put(`${API}/admin/reject-staff/${userId}`);
      toast.success('Staff application rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject staff');
    }
  };

  // Delete user (admin/secretary)
  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"? This will also delete all their vehicles, appointments, and related data.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`User "${userName}" deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Delete appointment/service (admin/secretary)
  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment/service?')) return;
    try {
      await axios.delete(`${API}/admin/appointments/${appointmentId}`);
      toast.success('Appointment deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete appointment');
    }
  };

  // Bulk delete users
  const bulkDeleteUsers = async (criteria, description) => {
    if (!window.confirm(`Are you sure you want to delete ${description}? This action cannot be undone.`)) return;
    try {
      const response = await axios.post(`${API}/admin/bulk-delete-users`, criteria);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk delete users');
    }
  };

  // Bulk delete appointments
  const bulkDeleteAppointments = async (criteria, description) => {
    if (!window.confirm(`Are you sure you want to delete ${description}? This action cannot be undone.`)) return;
    try {
      const response = await axios.post(`${API}/admin/bulk-delete-appointments`, criteria);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk delete appointments');
    }
  };

  // Product Management Functions
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProductFormData({ ...productFormData, image_url: response.data.image_url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const resetProductForm = () => {
    setProductFormData({
      category: 'engine_oil',
      brand_name: '',
      code: '',
      oil_type: '',
      price: '',
      image_url: ''
    });
    setEditingProduct(null);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    
    const payload = {
      category: productFormData.category,
      brand_name: productFormData.brand_name,
      code: productFormData.code || null,
      oil_type: productFormData.category === 'engine_oil' ? productFormData.oil_type : null,
      price: parseFloat(productFormData.price),
      image_url: productFormData.image_url || null
    };
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success('Product created successfully');
      }
      setProductDialogOpen(false);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      category: product.category,
      brand_name: product.brand_name,
      code: product.code || '',
      oil_type: product.oil_type || '',
      price: product.price.toString(),
      image_url: product.image_url || ''
    });
    setProductDialogOpen(true);
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const toggleProductStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { status: newStatus });
      toast.success(`Product ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  // Filter products based on search and category filter
  const filteredProducts = products.filter(p => {
    const matchesSearch = productSearch === '' || 
      p.brand_name.toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.code && p.code.toLowerCase().includes(productSearch.toLowerCase()));
    const matchesFilter = productFilter === 'all' || p.category === productFilter;
    return matchesSearch && matchesFilter;
  });

  const getCategoryLabel = (cat) => {
    const labels = {
      engine_oil: 'Engine Oil',
      oil_filter: 'Oil Filter',
      coolant: 'Coolant',
      brake_fluid: 'Brake Fluid',
      transmission_oil: 'Transmission Oil'
    };
    return labels[cat] || cat;
  };

  const getOilTypeLabel = (type) => {
    const labels = {
      synthetic: 'Synthetic',
      semi_synthetic: 'Semi-Synthetic',
      mineral: 'Mineral'
    };
    return labels[type] || type;
  };

  // Edit User Functions
  const openEditUserDialog = (user) => {
    setEditingUser(user);
    setEditUserFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || '',
      state: user.state || '',
      lga: user.lga || ''
    });
    setEditUserDialogOpen(true);
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, editUserFormData);
      toast.success('User updated successfully');
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Edit Merchant Functions
  const openEditMerchantDialog = (merchant) => {
    setEditingMerchant(merchant);
    setEditMerchantFormData({
      business_name: merchant.business_name || '',
      contact_person: merchant.contact_person || '',
      email: merchant.email || '',
      phone: merchant.phone || '',
      address: merchant.address || '',
      oil_brands: (merchant.oil_brands || []).join(', '),
      filter_brands: (merchant.filter_brands || []).join(', ')
    });
    setEditMerchantDialogOpen(true);
  };

  const saveMerchant = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editMerchantFormData,
        oil_brands: editMerchantFormData.oil_brands.split(',').map(b => b.trim()).filter(b => b),
        filter_brands: editMerchantFormData.filter_brands.split(',').map(b => b.trim()).filter(b => b)
      };
      await axios.put(`${API}/admin/merchants/${editingMerchant.id}`, payload);
      toast.success('Merchant updated successfully');
      setEditMerchantDialogOpen(false);
      setEditingMerchant(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update merchant');
    }
  };

  // Edit Organization Functions
  const openEditOrgDialog = (org) => {
    setEditingOrg(org);
    setEditOrgFormData({
      name: org.name || '',
      organization_type: org.organization_type || '',
      email: org.email || '',
      phone: org.phone || '',
      address: org.address || '',
      contact_person_name: org.contact_person_name || '',
      contact_person_phone: org.contact_person_phone || '',
      contact_person_email: org.contact_person_email || ''
    });
    setEditOrgDialogOpen(true);
  };

  const saveOrg = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/organizations/${editingOrg.id}`, editOrgFormData);
      toast.success('Organization updated successfully');
      setEditOrgDialogOpen(false);
      setEditingOrg(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        merchant_id: orderFormData.merchant_id,
        pickup_technician_id: orderFormData.pickup_technician_id || null,
        items: orderFormData.items.filter(i => i.brand),
        total_amount: orderFormData.total_amount ? parseFloat(orderFormData.total_amount) : null,
        notes: orderFormData.notes || null
      };
      await axios.post(`${API}/orders`, payload);
      toast.success('Order created successfully');
      setOrderDialogOpen(false);
      setOrderFormData({
        merchant_id: '',
        pickup_technician_id: '',
        items: [{ type: 'oil', brand: '', quantity: 1 }],
        total_amount: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const markOrderPaid = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/mark-paid`);
      toast.success('Payment marked as sent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark payment');
    }
  };

  const addOrderItem = () => {
    setOrderFormData({
      ...orderFormData,
      items: [...orderFormData.items, { type: 'oil', brand: '', quantity: 1 }]
    });
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderFormData.items];
    newItems[index][field] = field === 'quantity' ? parseInt(value) || 1 : value;
    setOrderFormData({ ...orderFormData, items: newItems });
  };

  const removeOrderItem = (index) => {
    if (orderFormData.items.length > 1) {
      setOrderFormData({
        ...orderFormData,
        items: orderFormData.items.filter((_, i) => i !== index)
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-xl font-heading font-bold text-foreground block">EXCEL ERA</span>
                <span className="text-xs text-secondary font-bold uppercase tracking-wide">Admin Panel</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="default" size="sm" />
              <Button 
                variant="ghost" 
                onClick={logout}
                data-testid="admin-logout-btn"
                className="font-bold uppercase text-sm"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Info Banner */}
        <div className="bg-primary text-white p-6 rounded-sm shadow-hard mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold mb-1">Admin Control Panel</h1>
              <p className="text-sm opacity-90">Manage appointments, technicians, and customer requests</p>
            </div>
            <div className="flex gap-3">
              <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-sm">
                <p className="text-xs uppercase font-bold opacity-75">Pending</p>
                <p className="text-2xl font-heading font-black">{stats?.pending_appointments || 0}</p>
              </div>
              <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-sm">
                <p className="text-xs uppercase font-bold opacity-75">Total</p>
                <p className="text-2xl font-heading font-black">{stats?.total_appointments || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-primary mb-2">{stats?.total_users || 0}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Users</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-secondary mb-2">{stats?.total_vehicles || 0}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Vehicles</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-foreground mb-2">{stats?.total_appointments || 0}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">All Appointments</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-yellow-600 mb-2">{stats?.pending_appointments || 0}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Pending</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList className="bg-white border-2 border-border p-1 flex-wrap">
            <TabsTrigger value="appointments" className="font-bold uppercase text-sm" data-testid="appointments-tab">Appointments</TabsTrigger>
            <TabsTrigger value="countdown" className="font-bold uppercase text-sm" data-testid="countdown-tab">Service Countdown</TabsTrigger>
            <TabsTrigger value="customers" className="font-bold uppercase text-sm" data-testid="customers-tab">Customers</TabsTrigger>
            <TabsTrigger value="products" className="font-bold uppercase text-sm" data-testid="products-tab">Products</TabsTrigger>
            <TabsTrigger value="staff" className="font-bold uppercase text-sm" data-testid="staff-tab">Staff</TabsTrigger>
            <TabsTrigger value="merchants" className="font-bold uppercase text-sm" data-testid="merchants-tab">Merchants</TabsTrigger>
            <TabsTrigger value="organizations" className="font-bold uppercase text-sm" data-testid="organizations-tab">Organizations</TabsTrigger>
            <TabsTrigger value="orders" className="font-bold uppercase text-sm" data-testid="orders-tab">Orders</TabsTrigger>
            <TabsTrigger value="technicians" className="font-bold uppercase text-sm" data-testid="technicians-tab">Ratings</TabsTrigger>
          </TabsList>

          {/* Service Countdown Tab */}
          <TabsContent value="countdown" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-heading font-bold text-foreground">Customer Service Countdown</h2>
            </div>

            {serviceCountdowns.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No customers with vehicles yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {serviceCountdowns.map((customer) => (
                  <div key={customer.customer_id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm" data-testid="countdown-customer-card">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-heading font-bold text-foreground">{customer.customer_name}</h3>
                        <p className="text-sm text-muted-foreground">{customer.customer_phone} • {customer.customer_email}</p>
                      </div>
                    </div>
                    
                    {customer.vehicles.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No vehicles registered</p>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {customer.vehicles.map((vehicle) => (
                          <div 
                            key={vehicle.vehicle_id}
                            className={`p-4 rounded-sm border-2 ${
                              vehicle.service_status === 'overdue' ? 'bg-red-50 border-red-300' :
                              vehicle.service_status === 'due_very_soon' ? 'bg-orange-50 border-orange-300' :
                              vehicle.service_status === 'due_soon' ? 'bg-yellow-50 border-yellow-300' :
                              vehicle.service_status === 'on_track' ? 'bg-green-50 border-green-300' :
                              'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-foreground">{vehicle.make} {vehicle.model}</span>
                              <span className="text-xs font-mono bg-white px-2 py-1 rounded">{vehicle.license_plate}</span>
                            </div>
                            
                            {vehicle.days_until_service !== null ? (
                              <div>
                                <div className={`text-2xl font-heading font-black ${
                                  vehicle.service_status === 'overdue' ? 'text-red-600' :
                                  vehicle.service_status === 'due_very_soon' ? 'text-orange-600' :
                                  vehicle.service_status === 'due_soon' ? 'text-yellow-600' :
                                  'text-green-600'
                                }`}>
                                  {vehicle.service_status === 'overdue' ? (
                                    <span>{Math.abs(vehicle.days_until_service)} days overdue</span>
                                  ) : (
                                    <span>{vehicle.days_until_service} days</span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Next service: {vehicle.next_service_date}
                                </p>
                                {vehicle.last_oil_type && (
                                  <p className="text-xs text-muted-foreground">
                                    Oil type: {vehicle.last_oil_type}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No service history</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-heading font-bold text-foreground">Manage Appointments</h2>
            </div>

            {appointments.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No appointments yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((appointment) => {
                  const user = users.find(u => u.id === appointment.user_id);
                  const assignedTech = technicians.find(t => t.id === appointment.technician_id);

                  return (
                    <div key={appointment.id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm" data-testid="admin-appointment-card">
                      <div className="grid md:grid-cols-4 gap-6">
                        <div className="md:col-span-2">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-xl font-heading font-bold text-foreground">{appointment.service_type}</h3>
                            <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                              appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              appointment.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`} data-testid="admin-appointment-status">
                              {appointment.status}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm font-medium">
                            <p><span className="text-muted-foreground">Customer:</span> <span className="font-bold text-foreground">{user?.name}</span></p>
                            <p><span className="text-muted-foreground">Phone:</span> <span className="font-mono text-foreground">{user?.phone}</span></p>
                            <p><span className="text-muted-foreground">Date:</span> <span className="font-bold text-foreground">{formatDate(appointment.date)}</span></p>
                            <p><span className="text-muted-foreground">Time:</span> <span className="font-bold text-foreground">{appointment.time}</span></p>
                            {appointment.notes && (
                              <p className="text-muted-foreground italic">&quot;{appointment.notes}&quot;</p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <Label className="font-bold uppercase text-xs tracking-wide mb-2 block">Status</Label>
                            <Select
                              value={appointment.status}
                              onValueChange={(value) => updateAppointment(appointment.id, value, appointment.technician_id)}
                            >
                              <SelectTrigger className="input-field" data-testid="status-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                            <div>
                              <Label htmlFor={`next-service-${appointment.id}`} className="font-bold uppercase text-xs tracking-wide mb-2 block">
                                Next Service Date
                              </Label>
                              <Input
                                id={`next-service-${appointment.id}`}
                                type="date"
                                data-testid="next-service-date-input"
                                className="input-field"
                                min={new Date().toISOString().split('T')[0]}
                                value={nextServiceDates[appointment.id] || ''}
                                onChange={(e) => setNextServiceDates(prev => ({ ...prev, [appointment.id]: e.target.value }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">Set before marking as completed</p>
                            </div>
                          )}
                        </div>

                        <div>
                          <Label className="font-bold uppercase text-xs tracking-wide mb-2 block">Assign Technician</Label>
                          <Select
                            value={appointment.technician_id || "unassigned"}
                            onValueChange={(value) => {
                              if (value !== "unassigned") {
                                updateAppointment(appointment.id, appointment.status, value);
                              }
                            }}
                          >
                            <SelectTrigger className="input-field" data-testid="technician-select">
                              <SelectValue>
                                {assignedTech ? assignedTech.name : 'Not Assigned'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Not Assigned</SelectItem>
                              {technicians.map(tech => (
                                <SelectItem key={tech.id} value={tech.id}>
                                  {tech.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          className="w-full"
                          onClick={() => deleteAppointment(appointment.id)}
                          data-testid="delete-appointment-btn"
                        >
                          Delete Appointment
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bulk Delete Actions */}
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-sm">
              <h3 className="text-lg font-bold text-red-800 mb-3">Bulk Actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => bulkDeleteAppointments({ status: 'pending' }, 'all pending appointments')}
                >
                  Delete All Pending
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => bulkDeleteAppointments({ status: 'completed' }, 'all completed appointments')}
                >
                  Delete All Completed
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => bulkDeleteAppointments({ status: 'cancelled' }, 'all cancelled appointments')}
                >
                  Delete All Cancelled
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-4">
            <h2 className="text-3xl font-heading font-bold text-foreground">Registered Customers</h2>

            {users.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No customers yet</p>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="customers-table">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Name</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Email</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Phone</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Joined</th>
                        <th className="px-6 py-4 text-center font-bold uppercase text-sm tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {users.map(user => (
                        <tr key={user.id} className="hover:bg-accent transition-colors">
                          <td className="px-6 py-4 font-bold text-foreground">{user.name}</td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{user.email}</td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{user.phone}</td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openEditUserDialog(user)}
                                data-testid="edit-user-btn"
                              >
                                Edit
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteUser(user.id, user.name)}
                                data-testid="delete-user-btn"
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Edit User Dialog */}
            <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
              <DialogContent className="bg-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                    Edit Customer
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={saveUser} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-bold uppercase text-sm">Name</Label>
                      <Input
                        className="input-field mt-2"
                        value={editUserFormData.name}
                        onChange={(e) => setEditUserFormData({ ...editUserFormData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Email</Label>
                      <Input
                        type="email"
                        className="input-field mt-2"
                        value={editUserFormData.email}
                        onChange={(e) => setEditUserFormData({ ...editUserFormData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-bold uppercase text-sm">Phone</Label>
                      <Input
                        className="input-field mt-2"
                        value={editUserFormData.phone}
                        onChange={(e) => setEditUserFormData({ ...editUserFormData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Address</Label>
                      <Input
                        className="input-field mt-2"
                        value={editUserFormData.address}
                        onChange={(e) => setEditUserFormData({ ...editUserFormData, address: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary w-full">Save Changes</Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Bulk Delete Customers */}
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-sm">
              <h3 className="text-lg font-bold text-red-800 mb-3">Bulk Actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => bulkDeleteUsers({ email_contains: 'test' }, 'all users with "test" in email')}
                >
                  Delete Test Accounts
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => bulkDeleteUsers({ account_status: 'pending' }, 'all pending accounts')}
                >
                  Delete All Pending
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Products Management Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <h2 className="text-3xl font-heading font-bold text-foreground">Product Catalog</h2>
              <Dialog open={productDialogOpen} onOpenChange={(open) => {
                setProductDialogOpen(open);
                if (!open) resetProductForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-product-btn">
                    + Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                      {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={saveProduct} className="space-y-4">
                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Category</Label>
                      <Select
                        value={productFormData.category}
                        onValueChange={(value) => setProductFormData({ ...productFormData, category: value, oil_type: '' })}
                      >
                        <SelectTrigger className="input-field mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {productCategories.categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Brand Name</Label>
                      <Input
                        className="input-field mt-2"
                        value={productFormData.brand_name}
                        onChange={(e) => setProductFormData({ ...productFormData, brand_name: e.target.value })}
                        placeholder="e.g., Mobil 1, Bosch, Total"
                        required
                      />
                    </div>
                    
                    {(productFormData.category === 'engine_oil' || productFormData.category === 'transmission_oil') && (
                      <div>
                        <Label className="font-bold uppercase text-sm tracking-wide">Code/Grade</Label>
                        <Input
                          className="input-field mt-2"
                          value={productFormData.code}
                          onChange={(e) => setProductFormData({ ...productFormData, code: e.target.value })}
                          placeholder="e.g., 5W-30, ATF"
                        />
                      </div>
                    )}
                    
                    {productFormData.category === 'engine_oil' && (
                      <div>
                        <Label className="font-bold uppercase text-sm tracking-wide">Oil Type</Label>
                        <Select
                          value={productFormData.oil_type}
                          onValueChange={(value) => setProductFormData({ ...productFormData, oil_type: value })}
                        >
                          <SelectTrigger className="input-field mt-2">
                            <SelectValue placeholder="Select oil type" />
                          </SelectTrigger>
                          <SelectContent>
                            {productCategories.oil_types.map(type => (
                              <SelectItem key={type} value={type}>{getOilTypeLabel(type)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Price (₦)</Label>
                      <Input
                        className="input-field mt-2"
                        type="number"
                        value={productFormData.price}
                        onChange={(e) => setProductFormData({ ...productFormData, price: e.target.value })}
                        placeholder="Enter price"
                        required
                        min="0"
                      />
                    </div>
                    
                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Product Image</Label>
                      <div className="mt-2 space-y-2">
                        <Input
                          className="input-field"
                          value={productFormData.image_url}
                          onChange={(e) => setProductFormData({ ...productFormData, image_url: e.target.value })}
                          placeholder="Paste image URL or upload below"
                        />
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="input-field"
                            disabled={uploadingImage}
                          />
                          {uploadingImage && <span className="text-sm text-muted-foreground">Uploading...</span>}
                        </div>
                        {productFormData.image_url && (
                          <img 
                            src={productFormData.image_url.startsWith('/') ? `${API.replace('/api', '')}${productFormData.image_url}` : productFormData.image_url} 
                            alt="Preview" 
                            className="w-24 h-24 object-cover rounded border"
                          />
                        )}
                      </div>
                    </div>
                    
                    <Button type="submit" className="btn-primary w-full">
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  className="input-field"
                  placeholder="Search products by name or code..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  data-testid="product-search"
                />
              </div>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="input-field w-full md:w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {productCategories.categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Service Charge Info */}
            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-sm mb-6">
              <p className="font-bold text-blue-800">
                Base Service Charge: ₦{productCategories.service_charge?.toLocaleString() || '10,000'}
              </p>
              <p className="text-sm text-blue-600">This is added to all bookings on top of selected products</p>
            </div>
            
            {/* Products List */}
            {filteredProducts.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No products found</p>
                <p className="text-sm text-muted-foreground mt-2">Add products to your catalog to get started</p>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="products-table">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold uppercase text-sm">Image</th>
                        <th className="px-4 py-3 text-left font-bold uppercase text-sm">Product</th>
                        <th className="px-4 py-3 text-left font-bold uppercase text-sm">Category</th>
                        <th className="px-4 py-3 text-left font-bold uppercase text-sm">Type/Code</th>
                        <th className="px-4 py-3 text-left font-bold uppercase text-sm">Price</th>
                        <th className="px-4 py-3 text-left font-bold uppercase text-sm">Status</th>
                        <th className="px-4 py-3 text-center font-bold uppercase text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredProducts.map(product => (
                        <tr key={product.id} className="hover:bg-accent transition-colors">
                          <td className="px-4 py-3">
                            {product.image_url ? (
                              <img 
                                src={product.image_url.startsWith('/') ? `${API.replace('/api', '')}${product.image_url}` : product.image_url}
                                alt={product.brand_name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                                No img
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 font-bold text-foreground">{product.brand_name}</td>
                          <td className="px-4 py-3 text-sm">{getCategoryLabel(product.category)}</td>
                          <td className="px-4 py-3 text-sm">
                            {product.oil_type && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-1">{getOilTypeLabel(product.oil_type)}</span>}
                            {product.code && <span className="font-mono">{product.code}</span>}
                          </td>
                          <td className="px-4 py-3 font-bold text-green-600">₦{product.price.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {product.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => editProduct(product)}
                              >
                                Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => toggleProductStatus(product)}
                              >
                                {product.status === 'active' ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteProduct(product.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Staff Management Tab */}
          <TabsContent value="staff" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-heading font-bold text-foreground">Staff Management</h2>
              <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-staff-btn">
                    + Create Staff Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold text-foreground">Create Staff Account</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={addStaff} className="space-y-4">
                    <div>
                      <Label htmlFor="staff_role" className="font-bold uppercase text-sm tracking-wide">Role</Label>
                      <Select
                        value={staffFormData.role}
                        onValueChange={(value) => setStaffFormData({ ...staffFormData, role: value })}
                      >
                        <SelectTrigger className="input-field mt-2" data-testid="staff-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technician">Technician</SelectItem>
                          <SelectItem value="secretary">Secretary</SelectItem>
                          <SelectItem value="marketer">Marketer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Technician Type Selection - Only shown for technician role */}
                    {staffFormData.role === 'technician' && (
                      <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-sm">
                        <Label className="font-bold uppercase text-sm tracking-wide text-blue-800 mb-3 block">Technician Type</Label>
                        <div className="space-y-2">
                          <div 
                            onClick={() => setStaffFormData({ ...staffFormData, technician_type: 'in-house' })}
                            className={`p-3 rounded cursor-pointer border-2 transition-all ${
                              staffFormData.technician_type === 'in-house' 
                                ? 'border-blue-500 bg-white' 
                                : 'border-gray-200 bg-white hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-bold text-foreground">In-House</span>
                                <p className="text-xs text-muted-foreground mt-1">₦500 bonus per vehicle serviced</p>
                              </div>
                              {staffFormData.technician_type === 'in-house' && (
                                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                          <div 
                            onClick={() => setStaffFormData({ ...staffFormData, technician_type: 'outsourced' })}
                            className={`p-3 rounded cursor-pointer border-2 transition-all ${
                              staffFormData.technician_type === 'outsourced' 
                                ? 'border-blue-500 bg-white' 
                                : 'border-gray-200 bg-white hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-bold text-foreground">Outsourced</span>
                                <p className="text-xs text-muted-foreground mt-1">₦6,000 (1st car) / ₦4,000 (subsequent)</p>
                              </div>
                              {staffFormData.technician_type === 'outsourced' && (
                                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="staff_name" className="font-bold uppercase text-sm tracking-wide">Name</Label>
                      <Input
                        id="staff_name"
                        required
                        data-testid="staff-name-input"
                        className="input-field mt-2"
                        value={staffFormData.name}
                        onChange={(e) => setStaffFormData({ ...staffFormData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="staff_email" className="font-bold uppercase text-sm tracking-wide">Email (Login ID)</Label>
                      <Input
                        id="staff_email"
                        type="email"
                        required
                        data-testid="staff-email-input"
                        className="input-field mt-2"
                        value={staffFormData.email}
                        onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="staff_phone" className="font-bold uppercase text-sm tracking-wide">Phone</Label>
                      <Input
                        id="staff_phone"
                        type="tel"
                        required
                        data-testid="staff-phone-input"
                        className="input-field mt-2"
                        placeholder="+234 XXX XXX XXXX"
                        value={staffFormData.phone}
                        onChange={(e) => setStaffFormData({ ...staffFormData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="staff_password" className="font-bold uppercase text-sm tracking-wide">Password</Label>
                      <Input
                        id="staff_password"
                        type="password"
                        required
                        data-testid="staff-password-input"
                        className="input-field mt-2"
                        placeholder="Minimum 6 characters"
                        value={staffFormData.password}
                        onChange={(e) => setStaffFormData({ ...staffFormData, password: e.target.value })}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isAddingStaff}
                      data-testid="staff-submit-btn"
                      className="btn-primary w-full h-12"
                    >
                      {isAddingStaff ? 'Creating...' : 'Create Account'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Pending Staff Approvals */}
            {pendingStaff.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-heading font-bold text-yellow-700 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></span>
                  Pending Staff Approvals ({pendingStaff.length})
                </h3>
                <div className="space-y-4">
                  {pendingStaff.map(person => (
                    <div key={person.id} className="bg-white border-l-4 border-yellow-500 p-6 rounded-sm shadow-sm" data-testid="pending-staff-card">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-xl font-heading font-bold text-foreground">{person.name}</h4>
                            <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                              person.role === 'technician' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {person.role}
                            </span>
                            {person.technician_type && (
                              <span className="text-xs font-bold px-2 py-1 rounded bg-gray-100 text-gray-700">
                                {person.technician_type}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{person.email} • {person.phone}</p>
                          {person.state && (
                            <p className="text-sm text-muted-foreground mt-1">
                              📍 {person.lga}, {person.state}
                            </p>
                          )}
                          {person.address && (
                            <p className="text-sm text-muted-foreground">{person.address}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">
                            Applied: {new Date(person.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => approveStaff(person.id)} 
                            className="bg-green-600 hover:bg-green-700 text-white font-bold"
                            data-testid="approve-staff-btn"
                          >
                            ✓ Approve
                          </Button>
                          <Button 
                            onClick={() => rejectStaff(person.id)} 
                            variant="outline" 
                            className="border-red-300 text-red-600 hover:bg-red-50 font-bold"
                          >
                            ✗ Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Staff */}
            <h3 className="text-xl font-heading font-bold text-foreground mb-4">Active Staff</h3>
            {staff.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground mb-4">No staff members yet</p>
                <Button onClick={() => setStaffDialogOpen(true)} className="btn-primary">
                  Create First Staff Account
                </Button>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="staff-table">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Name</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Role</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Email</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Phone</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Status</th>
                        <th className="px-6 py-4 text-center font-bold uppercase text-sm tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {staff.map(member => (
                        <tr key={member.id} className="hover:bg-accent transition-colors">
                          <td className="px-6 py-4 font-bold text-foreground">{member.name}</td>
                          <td className="px-6 py-4">
                            <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                              member.role === 'technician' ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'
                            }`}>
                              {member.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{member.email}</td>
                          <td className="px-6 py-4 font-mono text-sm text-foreground">{member.phone}</td>
                          <td className="px-6 py-4">
                            <span className="text-xs font-bold px-3 py-1 rounded-sm uppercase bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openEditUserDialog(member)}
                              >
                                Edit
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteUser(member.id, member.name)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Technicians Tab */}
          <TabsContent value="technicians" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-heading font-bold text-foreground">Manage Technicians</h2>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-technician-btn">
                    + Add Technician
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold text-foreground">Add New Technician</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={addTechnician} className="space-y-4">
                    <div>
                      <Label htmlFor="tech_name" className="font-bold uppercase text-sm tracking-wide">Name</Label>
                      <Input
                        id="tech_name"
                        required
                        data-testid="tech-name-input"
                        className="input-field mt-2"
                        value={techFormData.name}
                        onChange={(e) => setTechFormData({ ...techFormData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tech_phone" className="font-bold uppercase text-sm tracking-wide">Phone</Label>
                      <Input
                        id="tech_phone"
                        type="tel"
                        required
                        data-testid="tech-phone-input"
                        className="input-field mt-2"
                        placeholder="+234 XXX XXX XXXX"
                        value={techFormData.phone}
                        onChange={(e) => setTechFormData({ ...techFormData, phone: e.target.value })}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isAddingTech}
                      data-testid="tech-submit-btn"
                      className="btn-primary w-full h-12"
                    >
                      {isAddingTech ? 'Adding...' : 'Add Technician'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {technicians.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground mb-4">No technicians yet</p>
                <Button onClick={() => setDialogOpen(true)} className="btn-primary">
                  Add First Technician
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {technicians.map(tech => {
                  const ratings = technicianRatings[tech.id];
                  return (
                    <div key={tech.id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm" data-testid="technician-card">
                      <h3 className="text-xl font-heading font-bold text-foreground mb-2">{tech.name}</h3>
                      <p className="text-sm font-mono text-muted-foreground mb-3">{tech.phone}</p>
                      <span className={`inline-block text-xs font-bold px-3 py-1 rounded-sm uppercase mb-4 ${
                        tech.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tech.status}
                      </span>
                      
                      {ratings && ratings.total_ratings > 0 ? (
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map(star => (
                                <svg
                                  key={star}
                                  className={`w-5 h-5 ${star <= Math.round(ratings.overall_average) ? 'text-yellow-500' : 'text-gray-300'}`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <span className="text-sm font-bold text-foreground">{ratings.overall_average.toFixed(1)}</span>
                            <span className="text-xs text-muted-foreground">({ratings.total_ratings} ratings)</span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Skills:</span>
                              <span className="font-bold text-foreground">{ratings.average_skills.toFixed(1)}/5</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Conduct:</span>
                              <span className="font-bold text-foreground">{ratings.average_conduct.toFixed(1)}/5</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Neatness:</span>
                              <span className="font-bold text-foreground">{ratings.average_neatness.toFixed(1)}/5</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">No ratings yet</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Merchants Tab */}
          <TabsContent value="merchants" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-heading font-bold text-foreground">Merchant Management</h2>
            </div>

            {/* Pending Merchants */}
            {merchants.filter(m => m.status === 'pending').length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-heading font-bold text-yellow-700 mb-4">Pending Approval</h3>
                <div className="space-y-4">
                  {merchants.filter(m => m.status === 'pending').map(merchant => (
                    <div key={merchant.id} className="bg-white border-l-4 border-yellow-500 p-6 rounded-sm shadow-sm" data-testid="pending-merchant-card">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="text-xl font-heading font-bold text-foreground">{merchant.business_name}</h4>
                          <p className="text-sm text-muted-foreground">{merchant.contact_person} • {merchant.phone}</p>
                          <p className="text-sm text-muted-foreground">{merchant.email}</p>
                          <p className="text-sm text-muted-foreground mt-1">{merchant.address}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {merchant.oil_brands?.map((b, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded">{b}</span>
                            ))}
                            {merchant.filter_brands?.map((b, i) => (
                              <span key={i} className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">{b}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => approveMerchant(merchant.id)} className="bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="approve-merchant-btn">
                            Approve
                          </Button>
                          <Button onClick={() => rejectMerchant(merchant.id)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 font-bold">
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approved Merchants */}
            <h3 className="text-xl font-heading font-bold text-foreground mb-4">Approved Merchants</h3>
            {merchants.filter(m => m.status === 'approved').length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No approved merchants yet</p>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="merchants-table">
                    <thead className="bg-secondary text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Business</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Contact</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Products</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {merchants.filter(m => m.status === 'approved').map(merchant => (
                        <tr key={merchant.id} className="hover:bg-accent transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-bold text-foreground">{merchant.business_name}</div>
                            <div className="text-sm text-muted-foreground">{merchant.address}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium">{merchant.contact_person}</div>
                            <div className="text-sm text-muted-foreground">{merchant.phone}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {merchant.oil_brands?.slice(0, 2).map((b, i) => (
                                <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">{b}</span>
                              ))}
                              {merchant.filter_brands?.slice(0, 2).map((b, i) => (
                                <span key={i} className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">{b}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <Button onClick={() => openEditMerchantDialog(merchant)} variant="outline" size="sm">
                                Edit
                              </Button>
                              <Button onClick={() => deregisterMerchant(merchant.id)} variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50">
                                Deregister
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Edit Merchant Dialog */}
            <Dialog open={editMerchantDialogOpen} onOpenChange={setEditMerchantDialogOpen}>
              <DialogContent className="bg-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                    Edit Merchant
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={saveMerchant} className="space-y-4">
                  <div>
                    <Label className="font-bold uppercase text-sm">Business Name</Label>
                    <Input
                      className="input-field mt-2"
                      value={editMerchantFormData.business_name}
                      onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, business_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-bold uppercase text-sm">Contact Person</Label>
                      <Input
                        className="input-field mt-2"
                        value={editMerchantFormData.contact_person}
                        onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, contact_person: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Phone</Label>
                      <Input
                        className="input-field mt-2"
                        value={editMerchantFormData.phone}
                        onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm">Email</Label>
                    <Input
                      type="email"
                      className="input-field mt-2"
                      value={editMerchantFormData.email}
                      onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm">Address</Label>
                    <Input
                      className="input-field mt-2"
                      value={editMerchantFormData.address}
                      onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, address: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm">Oil Brands (comma-separated)</Label>
                    <Input
                      className="input-field mt-2"
                      value={editMerchantFormData.oil_brands}
                      onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, oil_brands: e.target.value })}
                      placeholder="Mobil, Total, Shell"
                    />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm">Filter Brands (comma-separated)</Label>
                    <Input
                      className="input-field mt-2"
                      value={editMerchantFormData.filter_brands}
                      onChange={(e) => setEditMerchantFormData({ ...editMerchantFormData, filter_brands: e.target.value })}
                      placeholder="Bosch, Mann, Fram"
                    />
                  </div>
                  <Button type="submit" className="btn-primary w-full">Save Changes</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Organizations Tab */}
          <TabsContent value="organizations" className="space-y-4">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-6">Organization Management</h2>
            
            {organizations.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No organizations registered yet</p>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="organizations-table">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Name</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Type</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Contact</th>
                        <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Email</th>
                        <th className="px-6 py-4 text-center font-bold uppercase text-sm tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {organizations.map(org => (
                        <tr key={org.id} className="hover:bg-accent transition-colors">
                          <td className="px-6 py-4 font-bold text-foreground">{org.name}</td>
                          <td className="px-6 py-4">
                            <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                              org.organization_type === 'government' ? 'bg-blue-100 text-blue-800' :
                              org.organization_type === 'private' ? 'bg-green-100 text-green-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {org.organization_type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium">{org.contact_person_name}</div>
                            <div className="text-xs text-muted-foreground">{org.contact_person_phone}</div>
                          </td>
                          <td className="px-6 py-4 font-mono text-sm">{org.email}</td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openEditOrgDialog(org)}
                              >
                                Edit
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Edit Organization Dialog */}
            <Dialog open={editOrgDialogOpen} onOpenChange={setEditOrgDialogOpen}>
              <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading font-bold text-foreground">
                    Edit Organization
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={saveOrg} className="space-y-4">
                  <div>
                    <Label className="font-bold uppercase text-sm">Organization Name</Label>
                    <Input
                      className="input-field mt-2"
                      value={editOrgFormData.name}
                      onChange={(e) => setEditOrgFormData({ ...editOrgFormData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm">Type</Label>
                    <Select
                      value={editOrgFormData.organization_type}
                      onValueChange={(v) => setEditOrgFormData({ ...editOrgFormData, organization_type: v })}
                    >
                      <SelectTrigger className="input-field mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="ngo">NGO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-bold uppercase text-sm">Email</Label>
                      <Input
                        type="email"
                        className="input-field mt-2"
                        value={editOrgFormData.email}
                        onChange={(e) => setEditOrgFormData({ ...editOrgFormData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Phone</Label>
                      <Input
                        className="input-field mt-2"
                        value={editOrgFormData.phone}
                        onChange={(e) => setEditOrgFormData({ ...editOrgFormData, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-sm">Address</Label>
                    <Input
                      className="input-field mt-2"
                      value={editOrgFormData.address}
                      onChange={(e) => setEditOrgFormData({ ...editOrgFormData, address: e.target.value })}
                    />
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <p className="font-bold text-sm mb-3">Contact Person</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold uppercase text-xs">Name</Label>
                        <Input
                          className="input-field mt-1"
                          value={editOrgFormData.contact_person_name}
                          onChange={(e) => setEditOrgFormData({ ...editOrgFormData, contact_person_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="font-bold uppercase text-xs">Phone</Label>
                        <Input
                          className="input-field mt-1"
                          value={editOrgFormData.contact_person_phone}
                          onChange={(e) => setEditOrgFormData({ ...editOrgFormData, contact_person_phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="font-bold uppercase text-xs">Email</Label>
                      <Input
                        type="email"
                        className="input-field mt-1"
                        value={editOrgFormData.contact_person_email}
                        onChange={(e) => setEditOrgFormData({ ...editOrgFormData, contact_person_email: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary w-full">Save Changes</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-heading font-bold text-foreground">Merchant Orders</h2>
              <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-order-btn">
                    + Create Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold text-foreground">Create Merchant Order</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createOrder} className="space-y-4">
                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Select Merchant *</Label>
                      <Select value={orderFormData.merchant_id} onValueChange={(v) => setOrderFormData({ ...orderFormData, merchant_id: v })}>
                        <SelectTrigger className="input-field mt-2" data-testid="order-merchant-select">
                          <SelectValue placeholder="Choose merchant" />
                        </SelectTrigger>
                        <SelectContent>
                          {approvedMerchants.map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.business_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Assign Pickup Technician</Label>
                      <Select value={orderFormData.pickup_technician_id} onValueChange={(v) => setOrderFormData({ ...orderFormData, pickup_technician_id: v })}>
                        <SelectTrigger className="input-field mt-2">
                          <SelectValue placeholder="Select technician (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {staff.filter(s => s.role === 'technician').map(tech => (
                            <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-accent p-4 rounded-sm">
                      <div className="flex justify-between items-center mb-3">
                        <Label className="font-bold uppercase text-sm tracking-wide">Order Items</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addOrderItem}>+ Add Item</Button>
                      </div>
                      {orderFormData.items.map((item, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <Select value={item.type} onValueChange={(v) => updateOrderItem(index, 'type', v)}>
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="oil">Oil</SelectItem>
                              <SelectItem value="filter">Filter</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="Brand name"
                            value={item.brand}
                            onChange={(e) => updateOrderItem(index, 'brand', e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)}
                            className="w-20"
                          />
                          {orderFormData.items.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeOrderItem(index)} className="text-red-600">×</Button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Total Amount (₦)</Label>
                      <Input
                        type="number"
                        className="input-field mt-2"
                        placeholder="Enter total amount"
                        value={orderFormData.total_amount}
                        onChange={(e) => setOrderFormData({ ...orderFormData, total_amount: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label className="font-bold uppercase text-sm tracking-wide">Notes</Label>
                      <Input
                        className="input-field mt-2"
                        placeholder="Any special instructions"
                        value={orderFormData.notes}
                        onChange={(e) => setOrderFormData({ ...orderFormData, notes: e.target.value })}
                      />
                    </div>

                    <Button type="submit" className="btn-primary w-full h-12" disabled={!orderFormData.merchant_id}>
                      Create Order
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {orders.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="bg-white border-2 border-border p-6 rounded-sm" data-testid="order-card">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'ready_for_pickup' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'picked_up' ? 'bg-orange-100 text-orange-800' :
                            order.status === 'delivered' ? 'bg-purple-100 text-purple-800' :
                            order.status === 'paid' ? 'bg-green-100 text-green-800' :
                            'bg-green-200 text-green-900'
                          }`}>
                            {order.status.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-muted-foreground">#{order.id.slice(0, 8)}</span>
                        </div>
                        <p className="font-bold text-foreground">{order.merchant_name}</p>
                        <div className="text-sm mt-2">
                          {order.items?.map((item, i) => (
                            <span key={i} className="mr-2">{item.quantity}x {item.brand} ({item.type})</span>
                          ))}
                        </div>
                        {order.total_amount && <p className="text-lg font-bold text-green-600 mt-1">₦{order.total_amount.toLocaleString()}</p>}
                        {order.technician_name && <p className="text-sm text-muted-foreground mt-1">Pickup: {order.technician_name}</p>}
                      </div>
                      <div className="flex items-start">
                        {order.status === 'delivered' && (
                          <Button onClick={() => markOrderPaid(order.id)} className="bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="mark-paid-btn">
                            Mark Payment Sent
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
